import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  People,
  CalendarToday,
  AccountBalanceWallet,
  Search,
} from "@mui/icons-material";
import api from "../api/api";
import CustomSnackbar from "../components/CustomSnackbar";
import en_ja from "../en-ja-map";

const Dashboard = () => {
  const [loading, setLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    appointmentCount: 0,
    totalPatients: 0,
    totalRevenue: 0,
    payments: [],
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("Transaction Date");
  const [order, setOrder] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const user = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    fetchPaymentHistory();
  }, []);

  const fetchPaymentHistory = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/v1/clinic/user/paymet-history/${user.id}`);
      console.log("Payment history response:", response.data);
      if (response.data) {
        setDashboardData({
          appointmentCount: response.data.appointmentCount || 0,
          totalPatients: response.data.totalPatients || 0,
          totalRevenue: response.data.totalRevenue || 0,
          payments: response.data.payments || [],
        });
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching payment history:", error);
      setSnackbarMessage(en_ja["Error loading dashboard data"] || "Error loading dashboard data");
      setSnackbarType("error");
      setSnackbarOpen(true);
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
    setSnackbarMessage("");
    setSnackbarType("success");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Format date to YYYY/MM/DD (Japanese standard)
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}/${month}/${day}`;
    } catch (error) {
      return "-";
    }
  };

  // Format revenue to show currency
  const formatRevenue = (amount) => {
    if (amount === 0) return "¥0";
    return `¥${amount.toLocaleString()}`;
  };

  // Filter payments based on search query
  const filteredPayments = dashboardData.payments.filter((payment) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      payment["User Name"]?.toLowerCase().includes(searchLower) ||
      payment["Payment Method"]?.toLowerCase().includes(searchLower) ||
      payment["Transaction ID"]?.toString().toLowerCase().includes(searchLower) ||
      payment["Oorder ID"]?.toString().toLowerCase().includes(searchLower)
    );
  });

  // Sort payments
  const sortedPayments = [...filteredPayments].sort((a, b) => {
    let aValue, bValue;

    switch (orderBy) {
      case "User Name":
        aValue = a["User Name"] || "";
        bValue = b["User Name"] || "";
        break;
      case "Transaction Date":
        aValue = new Date(a["Transaction Date"]).getTime();
        bValue = new Date(b["Transaction Date"]).getTime();
        break;
      case "Payment Method":
        aValue = a["Payment Method"] || "";
        bValue = b["Payment Method"] || "";
        break;
      case "Amount Paid":
        aValue = a["Amount Paid"] || 0;
        bValue = b["Amount Paid"] || 0;
        break;
      default:
        return 0;
    }

    if (order === "asc") {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Paginate payments
  const paginatedPayments = sortedPayments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const StatCard = ({ title, value, subtitle, icon, iconBg }) => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: "16px",
        border: "1px solid #e9ecef",
        backgroundColor: "white",
        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
          transform: "translateY(-4px)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            color="#6c757d"
            sx={{ fontSize: "14px", fontWeight: "500", mb: 1 }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            fontWeight="700"
            color="#1a1a1a"
            sx={{ fontSize: "28px", mb: 0.5 }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography
              variant="body2"
              color="#6c757d"
              sx={{ fontSize: "12px", mb: 1 }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "12px",
            backgroundColor: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Box
      sx={{
        p: 3,
        maxWidth: "100%",
        backgroundColor: "white",
        minHeight: "100vh",
      }}
    >
      <CustomSnackbar
        isOpen={snackbarOpen}
        message={snackbarMessage}
        onClose={handleCloseSnackbar}
        type={snackbarType}
      />

      {loading && (
        <Box sx={{ width: "100%", mb: 2 }}>
          <LinearProgress />
        </Box>
      )}

      {/* Stats Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: 3,
          mb: 4,
        }}
      >
        <StatCard
          title={en_ja["Total Patients"] || "Total Patients"}
          value={dashboardData.totalPatients.toLocaleString()}
          subtitle={en_ja["Registered patients"] || "Registered patients"}
          icon={<People sx={{ color: "white", fontSize: 24 }} />}
          iconBg="#dc3545"
        />
        <StatCard
          title={en_ja["Total Appointments"] || "Total Appointments"}
          value={dashboardData.appointmentCount.toLocaleString()}
          subtitle={en_ja["Total appointments"] || "Total appointments"}
          icon={<CalendarToday sx={{ color: "white", fontSize: 24 }} />}
          iconBg="#007bff"
        />
        <StatCard
          title={en_ja["Total Revenue"] || "Total Revenue"}
          value={formatRevenue(dashboardData.totalRevenue)}
          subtitle={en_ja["Total earnings"] || "Total earnings"}
          icon={<AccountBalanceWallet sx={{ color: "white", fontSize: 24 }} />}
          iconBg="#28a745"
        />
      </Box>

      {/* Payment History Table */}
      {dashboardData.payments.length > 0 ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: "16px",
            border: "1px solid #e9ecef",
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 3, borderBottom: "1px solid #e9ecef" }}>
            <Typography
              variant="h6"
              fontWeight="600"
              color="#1a1a1a"
              sx={{ fontSize: "18px", mb: 2 }}
            >
              {en_ja["Payment History"] || "Payment History"}
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={en_ja["Search by name, payment method..."] || "Search by name, payment method..."}
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: "#6c757d" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              }}
            />
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f8f9fa" }}>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "User Name"}
                      direction={orderBy === "User Name" ? order : "asc"}
                      onClick={() => handleRequestSort("User Name")}
                    >
                      <Typography fontWeight="600" fontSize="14px">
                        {en_ja["User Name"] || "User Name"}
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "Transaction Date"}
                      direction={orderBy === "Transaction Date" ? order : "asc"}
                      onClick={() => handleRequestSort("Transaction Date")}
                    >
                      <Typography fontWeight="600" fontSize="14px">
                        {en_ja["Transaction Date"] || "Transaction Date"}
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "Payment Method"}
                      direction={orderBy === "Payment Method" ? order : "asc"}
                      onClick={() => handleRequestSort("Payment Method")}
                    >
                      <Typography fontWeight="600" fontSize="14px">
                        {en_ja["Payment Method"] || "Payment Method"}
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "Amount Paid"}
                      direction={orderBy === "Amount Paid" ? order : "asc"}
                      onClick={() => handleRequestSort("Amount Paid")}
                    >
                      <Typography fontWeight="600" fontSize="14px">
                        {en_ja["Amount Paid"] || "Amount Paid"}
                      </Typography>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="600" fontSize="14px">
                      {en_ja["Order ID"] || "Order ID"}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedPayments.map((payment, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:hover": {
                        backgroundColor: "#f8f9fa",
                      },
                    }}
                  >
                    <TableCell>
                      <Typography fontSize="14px">
                        {payment["User Name"] || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize="14px">
                        {formatDate(payment["Transaction Date"])}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        fontSize="14px"
                        sx={{ textTransform: "capitalize" }}
                      >
                        {payment["Payment Method"] || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize="14px" fontWeight="600" color="#28a745">
                        {formatRevenue(payment["Amount Paid"])}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize="14px">
                        {payment["Order ID"] || "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredPayments.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            sx={{
              borderTop: "1px solid #e9ecef",
            }}
          />
        </Paper>
      ) : (
        !loading && (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              backgroundColor: "#f8f9fa",
              borderRadius: "12px",
              border: "1px solid #e9ecef",
            }}
          >
            <Typography
              variant="h6"
              color="#6c757d"
              sx={{ fontSize: "18px", fontWeight: "500", mb: 1 }}
            >
              {en_ja["No Payment History Available"] || "No Payment History Available"}
            </Typography>
            <Typography
              variant="body2"
              color="#6c757d"
              sx={{ fontSize: "14px" }}
            >
              {en_ja["Payment history will appear here once transactions are made."] ||
                "Payment history will appear here once transactions are made."}
            </Typography>
          </Box>
        )
      )}
    </Box>
  );
};

export default Dashboard;